import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import { FiShare2, FiPlus, FiSearch, FiTrendingUp, FiMail, FiClock, FiSliders, FiCheck, FiAlertCircle, FiHelpCircle, FiArrowRight } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { SocialAccountsResult } from '../../../model/SocialAccountsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import Dropdown from '../Dropdown';
import NewSocialPostModal from './NewSocialPostModal';

// one post made through Investi; nothing records these yet (X posts from the Announcements ⋯ menu aren't stored), so the list is always empty
interface SocialPostRow {
    id: string;
    text: string;
    source: string;
    channels: ('linkedin' | 'x')[];
    when: string;
    status: 'Scheduled' | 'Published' | 'Draft';
}

const CHANNELS = [{ value: 'All', label: 'Channel: All' }, { value: 'linkedin', label: 'Channel: LinkedIn' }, { value: 'x', label: 'Channel: X' }];
const STATUSES = ['All', 'Scheduled', 'Published', 'Draft'].map(x => ({ value: x, label: `Status: ${x}` }));
const tones: Record<string, string> = { Scheduled: 'info', Published: 'ok', Draft: '' };
// no source for post history yet — always empty, never sample rows
const POSTS: SocialPostRow[] = [];

// spec 213:8946 "v3 / Communications – Social posts" (LinkedIn and X; Facebook left out, user decision 2026-10-14).
// Real: the connected LinkedIn company and X account (GET /api/social/accounts).
// No source for post history, impressions, clicks or scheduling, so those show empty / "—". "Best time to post" omitted (no source).
const SocialPosts: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    // undefined while loading, null when the load failed
    const [accounts, setAccounts] = useState<SocialAccountsResult | null | undefined>(undefined);
    const x = accounts?.x ?? null;
    const linkedIn = accounts?.linkedIn?.companyName ? accounts.linkedIn : null;
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [channel, setChannel] = useState('All');
    const [status, setStatus] = useState('All');
    const [composing, setComposing] = useState(false);

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        setAccounts(undefined);
        const [err, data] = await to(api().getSocialAccounts(stock.id));
        if (!isMounted()) return;
        if (err) { setError('Couldn’t load the connected social accounts'); setAccounts(null); }
        else { setError(''); setAccounts(data as SocialAccountsResult); }
    }, [stock?.id]);

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        return POSTS.filter(p => (!q || p.text.toLowerCase().includes(q))
            && (channel === 'All' || p.channels.includes(channel as 'linkedin' | 'x'))
            && (status === 'All' || p.status === status));
    }, [query, channel, status]);

    const none = POSTS.length === 0;

    return <div className="social-posts">
        <div className="page-header">
            <div>
                <h1>Social Posts</h1>
                <p>Posts for LinkedIn and X, drafted from your announcements</p>
            </div>
            <div className="sp-actions">
                {(linkedIn || x) && <span className="pill ok sp-status"><FiCheck /> {[linkedIn && 'LinkedIn', x && 'X'].filter(Boolean).join(' and ')} connected</span>}
                {accounts && !linkedIn && !x && <span className="pill warn sp-status">No accounts connected</span>}
                <button type="button" className="btn-primary" onClick={() => setComposing(true)}><FiPlus /> New Post</button>
            </div>
        </div>

        {error && <p className="error">{error}</p>}

        <div className="sp-stats">
            <div className="sp-stat">
                <div className="sp-stat-h"><span className="icon-tile"><FiShare2 /></span> Posts this quarter</div>
                <div className="sp-stat-v">—</div>
                <div className="sp-stat-d">Posts aren’t recorded yet</div>
            </div>
            <div className="sp-stat">
                <div className="sp-stat-h"><span className="icon-tile"><FiTrendingUp /></span> Impressions</div>
                <div className="sp-stat-v">—</div>
                <div className="sp-stat-d">Not connected yet</div>
            </div>
            <div className="sp-stat">
                <div className="sp-stat-h"><span className="icon-tile"><FiMail /></span> Clicks to Website Hub</div>
                <div className="sp-stat-v">—</div>
                <div className="sp-stat-d">Not tracked yet</div>
            </div>
            <div className="sp-stat">
                <div className="sp-stat-h"><span className="icon-tile"><FiClock /></span> Scheduled</div>
                <div className="sp-stat-v">—</div>
                <div className="sp-stat-d">Scheduling isn’t available yet</div>
            </div>
        </div>

        <div className="sp-layout">
            <div className="card sp-posts">
                <div className="sp-filters">
                    <label className="sp-search"><FiSearch /><input placeholder="Search posts…" value={query} onChange={e => setQuery(e.target.value)} disabled={none} /></label>
                    <div className="sp-filter"><Dropdown value={channel} options={CHANNELS} onChange={setChannel} disabled={none} /></div>
                    <div className="sp-filter"><Dropdown value={status} options={STATUSES} onChange={setStatus} disabled={none} /></div>
                    <span className="sp-sorted">Sorted by date</span>
                </div>

                <div className="sp-table">
                    <div className="sp-row sp-head">
                        <span>Post</span><span>Channels</span><span>When</span><span>Status</span><span className="sp-num">Impressions</span><span className="sp-num">Clicks</span><span />
                    </div>
                    {rows.map(p => <div key={p.id} className="sp-row">
                        <div className="sp-post"><div className="sp-text">{p.text}</div><div className="sp-source">{p.source}</div></div>
                        <span className="sp-channels">{p.channels.map(c => <span key={c} className={`sp-net ${c}`} title={c === 'x' ? 'X' : 'LinkedIn'} />)}</span>
                        <span className="sp-when">{p.when}</span>
                        <span><span className={`pill ${tones[p.status]}`}>{p.status}</span></span>
                        <span className="sp-num">—</span>
                        <span className="sp-num">—</span>
                        <span />
                    </div>)}
                    {none && <div className="sp-empty">
                        <span className="icon-tile"><FiShare2 /></span>
                        <strong>No social posts yet</strong>
                        <p>Investi doesn’t keep a record of social posts yet. Posts sent to X from an announcement’s ⋯ menu go straight to X and won’t show here.</p>
                        <button type="button" className="btn-outline" onClick={() => setComposing(true)}><FiPlus /> New Post</button>
                    </div>}
                    {!none && rows.length === 0 && <p className="sp-nomatch">No posts match</p>}
                </div>
            </div>

            <div className="sp-side">
                <div className="card sp-settings">
                    <div className="sp-title">
                        <span className="icon-tile"><FiSliders /></span>
                        <div><h2>Social Settings</h2><p>Connected accounts and the automatic posting rules</p></div>
                    </div>
                    <ul className="sp-accounts">
                        {accounts === undefined && <li className="off"><FiClock /> Checking…</li>}
                        {accounts !== undefined && (linkedIn
                            ? <li><FiCheck /> LinkedIn — {linkedIn.companyName}</li>
                            : <li className="off"><FiAlertCircle /> LinkedIn — not connected</li>)}
                        {accounts !== undefined && (x
                            ? <li><FiCheck /> X — @{x.username}</li>
                            : <li className="off"><FiAlertCircle /> X — not connected</li>)}
                    </ul>
                    <div className="sp-note">
                        <span className="icon-tile"><FiHelpCircle /></span>
                        <p>Accounts and the automatic posting rules live in Client settings › Announcement settings › Social posts, so there is one place to change them.</p>
                    </div>
                    <Link className="link sp-open" to="/client-settings/announcements/social">Open Social Settings <FiArrowRight /></Link>
                </div>
            </div>
        </div>

        {composing && <NewSocialPostModal onClose={() => setComposing(false)} />}
    </div>;
};

export default SocialPosts;
